import { defineStore } from 'pinia';
import { OidcUser, User } from 'src/interfaces/models';
import { AxiosError, AxiosResponse } from 'axios';
import { AuthToken } from 'src/interfaces/auth';
import AuthService from 'src/services/AuthService';
import * as Sentry from '@sentry/vue';
import { useWebSocketsStore } from './sockets';
import { usePopupHandler } from 'src/composables/PopupHandler';
import { useEnvsStore } from 'stores/envs';

export interface AuthStateInterface {
  status: string;
  auth_token: AuthToken;
  user: User;
  oidc_user: OidcUser;
  guest_user: User;
  is_logged_in: boolean;
}

export const defaultUser = (): User => {
  return {
    id: null,
    uuid: null,
    url: '',
    first_name: '',
    last_name: '',
    email: '',
    mobile: '',
    groups: [],
    selected_groups: [],
    user_permissions: [],
    permissions: [],
    last_login: null,
    is_superuser: false,
    username: '',
    is_staff: false,
    is_active: false,
    date_joined: '',
    is_portal_user: false,
    title: '',
    address1: null,
    address2: null,
    city: null,
    county: null,
    nation: null,
    postcode: null,
    landline: null,
    nhs_number: null,
    gender: -1,
    created: '',
    modified: '',
    dob: null,
    gp_surgery_id: null,
    is_deceased: false,
    allergies: [],
  };
};

export const defaultOidcUser = (): OidcUser => {
  return {
    access_token: '',
    expires_at: null,
    profile: {
      sub: '',
    },
  };
};

export const useAuthStore = defineStore('auth', {
  state: (): AuthStateInterface => ({
    status: '',
    auth_token: {
      token: '',
      expiry: null,
      status: '',
      validated: false,
    },
    user: defaultUser(),
    oidc_user: defaultOidcUser(),
    guest_user: {} as User,
    is_logged_in: false,
  }),

  getters: {
    authenticated(): boolean {
      return this.is_logged_in;
    },

    hasPermission(state): (permission: string) => boolean {
      return (permission: string): boolean =>
        state.user.permissions.includes(permission);
    },

    isClinician(state): boolean {
      const { staff } = state.user;
      return staff ? !staff.is_deleted && staff.is_active : false;
    },

    hasGuestUser(state): boolean {
      if (state.guest_user?.guest_access_token) {
        return true;
      }

      try {
        const userSession = sessionStorage.getItem('guest_user');
        if (userSession) {
          const parsedUser = JSON.parse(userSession);
          return !!parsedUser?.guest_access_token;
        }
      } catch (e) {}
      return false;
    },
  },

  actions: {
    getUserDetails(initProgressSocket = true): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        await this.api
          .get<User>('private/user-auth/user_details/', {
            withCredentials: true,
          })
          .then((response: AxiosResponse) => {
            this.clearGuestUser();

            this.user = response.data;

            Sentry.setUser({
              email: response.data.email,
              name: response.data.full_name,
            });

            if (initProgressSocket) {
              const socketStore = useWebSocketsStore();

              socketStore.initProgressWebSocket();
            }
            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            this.$reset();
            reject(e);
          });
      });
    },

    updateUser(payload: User): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        await this.api
          .put<User>(`private/users/${payload.id}/`, payload)
          .then((response: AxiosResponse) => {
            if (response.status !== 400) {
              this.getUserDetails(false);
            }

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
      });
    },

    updateUserPartially(
      type: string,
      userId: number,
      params: object
    ): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        const url =
          type === 'clinic'
            ? `admin/users/${userId}/`
            : `private/users/${userId}/`;

        await this.api
          .patch<User>(url, params, { withCredentials: true })
          .then((response: AxiosResponse) => {
            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
      });
    },

    fetchGuestProfile(token: string): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        await this.api
          .get('public/guest-profile/', {
            headers: { 'Guest-User-Access-Token': token },
          })
          .then((response: AxiosResponse) => {
            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
      });
    },

    updateGuestProfile(
      token: string,
      userId: number,
      params: object
    ): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        await this.api
          .patch<User>(`public/users/${userId}/`, params, {
            headers: { 'Guest-User-Access-Token': token },
          })
          .then((response: AxiosResponse) => {
            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
      });
    },

    oidcLogout(): void {
      const authService = new AuthService(window.location.origin);
      const userStoreKey = authService.userStoreKey();

      this.$reset();

      localStorage.removeItem(userStoreKey);
      sessionStorage.removeItem(userStoreKey);

      Sentry.setUser(null);
      void this.router.replace({ path: '/login' });
    },

    setGuestUser(user: User): void {
      this.guest_user = user;
      sessionStorage.setItem('guest_user', JSON.stringify(user));
    },

    getGuestUserHeaders(): { 'Guest-User-Access-Token'?: string } {
      if (!this.guest_user) {
        try {
          const user = sessionStorage.getItem('guest_user');
          if (user) {
            this.guest_user = JSON.parse(user);
          }
        } catch (e) {}
      }

      if (this.guest_user?.guest_access_token) {
        return {
          'Guest-User-Access-Token': this.guest_user.guest_access_token,
        };
      }
      return {};
    },

    clearGuestUser(): void {
      sessionStorage.removeItem('guest_user');
      this.guest_user = {} as User;
    },

    checkGuestUserValidity(): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        const headers = this.getGuestUserHeaders();
        await this.api
          .get('public/check-guest-user/', { headers: headers })
          .then((response: AxiosResponse) => {
            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
      });
    },

    redirectToLogin() {
      const { openPopup } = usePopupHandler();
      const envStore = useEnvsStore();

      openPopup(`${envStore.API_BASE}private/login-callback/`);
    },

    redirectToLogout() {
      const { openPopup } = usePopupHandler();
      const envStore = useEnvsStore();

      openPopup(`${envStore.API_BASE}private/logout`, true);
    },

    checkLoggedIn(): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        await this.api
          .get('private/is-logged-in/', { withCredentials: true })
          .then((response: AxiosResponse) => {
            this.is_logged_in = true;

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            this.is_logged_in = false;
            this.user = defaultUser();

            reject(e);
          });
      });
    },
  },
});
